export type BrandConfig = {
  id: string;
  name: string;
  loginLogo: string;
  authLogo: string;
  authLogoBgClass?: string;
  sidebarIcon: string;
  sidebarText: string;
  favicon: string;
  loginBgClass: string;
  sidebarSwap: boolean;
};

const brands: Record<string, BrandConfig> = {
  predoc: {
    id: 'predoc',
    name: 'Predoc',
    loginLogo: '/full-logo.svg',
    authLogo: '/full-logo-black.svg',
    sidebarIcon: '/predoc_nav_icon.svg',
    sidebarText: '/predoc_text.svg',
    favicon: '/favicon.png',
    loginBgClass: 'bg-black',
    sidebarSwap: false,
  },
  recordrx: {
    id: 'recordrx',
    name: 'RecordRx',
    loginLogo: '/brands/recordrx/logo.png',
    authLogo: '/brands/recordrx/logo-black.png',
    sidebarIcon: '/brands/recordrx/icon.png',
    sidebarText: '/brands/recordrx/logo.png',
    favicon: '/brands/recordrx/favicon.png',
    loginBgClass: 'bg-black',
    sidebarSwap: true,
  },
};

/**
 * Maps the first segment of the hostname (subdomain) to a brand ID.
 * Strips environment suffixes (-dev, -stage) so that
 * recordrx-dev.predoc.ai and recordrx.predoc.ai both resolve to "recordrx".
 */
const domainToBrand: Record<string, string> = {
  recordrx: 'recordrx',
};

function extractBrandKey(hostname: string): string | undefined {
  const subdomain = hostname.split('.')[0];
  const normalized = subdomain.replace(/-(dev|stage|staging)$/, '');
  return domainToBrand[normalized];
}

let cachedBrand: BrandConfig | null = null;

export function resolveBrand(): BrandConfig {
  if (cachedBrand) return cachedBrand;

  // --- LOCAL TESTING: uncomment the next line, comment out the hostname block below ---
  // cachedBrand = brands['recordrx'];
  // return cachedBrand;

  // --- PRODUCTION: keep this block active ---
  const hostname = window.location.hostname;
  const brandKey = extractBrandKey(hostname);
  cachedBrand = brands[brandKey ?? 'predoc'] ?? brands.predoc;
  return cachedBrand;
  // --- END ---
}

export function getBrandById(id: string): BrandConfig {
  return brands[id] ?? brands.predoc;
}

/**
 * Manually-maintained whitelist of email domains that get custom branding.
 * Users whose email matches a domain here will see that brand's sidebar/favicon
 * even if they logged in through the default app.predoc.ai flow.
 */
const emailDomainToBrand: Record<string, string> = {
  'recordrx.com': 'recordrx',
};

export function resolveBrandByEmail(
  email: string | undefined,
): BrandConfig | null {
  if (!email) return null;
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return null;
  const brandKey = emailDomainToBrand[domain];
  return brandKey ? (brands[brandKey] ?? null) : null;
}
